import { useState } from 'react';
import type { ObsessLessUser } from '../types/commonTypes';
import { userService } from '../services/userService';

interface UserIdSearchProps {
    onSearchResult: (user: ObsessLessUser | null) => Promise<void>;
}

export const UserIdSearch = ({ onSearchResult }: UserIdSearchProps) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [searchResults, setSearchResults] = useState<ObsessLessUser[]>([]);
    const [showResults, setShowResults] = useState(false);

    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            setSearchError('Please enter a user ID or email');
            return;
        }

        setIsSearching(true);
        setSearchError(null);
        setShowResults(false);

        try {
            const users = await userService.searchUsers(searchQuery.trim());

            if (users.length === 0) {
                setSearchError('No users found matching your search');
                setSearchResults([]);
            } else if (users.length === 1) {
                // Single result - directly select it
                await onSearchResult(users[0]);
                setSearchQuery('');
                setSearchResults([]);
            } else {
                // Multiple results - show selection list
                setSearchResults(users);
                setShowResults(true);
            }
        } catch (error) {
            setSearchError('An error occurred while searching. Please try again.');
            console.error('Search error:', error);
        } finally {
            setIsSearching(false);
        }
    };

    const handleSelectUser = async (user: ObsessLessUser) => {
        await onSearchResult(user);
        setSearchQuery('');
        setSearchResults([]);
        setShowResults(false);
        setSearchError(null);
    };

    const handleCancelSelection = () => {
        setShowResults(false);
        setSearchResults([]);
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <div className="mb-4">
            <div className="flex gap-3 items-start">
                <div className="flex-1 min-w-0">
                    <input
                        type="text"
                        placeholder="Search by user ID or email..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            // Clear error when user starts typing
                            if (searchError) setSearchError(null);
                        }}
                        onKeyDown={handleKeyPress}
                        disabled={showResults}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    {searchError && (
                        <p className="text-red-600 text-sm mt-1">{searchError}</p>
                    )}
                </div>
                <button
                    onClick={handleSearch}
                    disabled={isSearching || showResults}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSearching ? 'Searching...' : 'Find User'}
                </button>
            </div>

            {/* Multiple results selection list */}
            {showResults && searchResults.length > 0 && (
                <div className="mt-4 border border-gray-300 rounded-lg bg-white shadow-lg">
                    <div className="p-4 bg-blue-50 border-b border-gray-300 flex flex-wrap justify-between items-center gap-2">
                        <h3 className="font-semibold text-gray-900">
                            Found {searchResults.length} users - Select one:
                        </h3>
                        <button
                            onClick={handleCancelSelection}
                            className="text-gray-600 hover:text-gray-900 text-sm underline"
                        >
                            Cancel
                        </button>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {searchResults.map((user) => (
                            <button
                                key={user.uid}
                                onClick={() => handleSelectUser(user)}
                                className="w-full p-4 text-left hover:bg-gray-100 border-b border-gray-200 last:border-b-0 transition-colors"
                            >
                                <div className="flex flex-col gap-1">
                                    <div className="font-semibold text-gray-900">
                                        {user.displayName || 'No name'}
                                    </div>
                                    <div className="text-sm text-gray-600 break-all">
                                        {user.email}
                                    </div>
                                    <div className="text-xs text-gray-500 font-mono break-all">
                                        ID: {user.uid}
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};